import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { motion } from 'framer-motion';
import {
  Check,
  HelpCircle,
  Calendar,
  Mail,
  BookOpen,
  UtensilsCrossed,
  CalendarDays,
  Images,
  Sparkles,
  ShoppingBag,
  ArrowDown,
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: 'easeOut' }
};

const packages = [
  {
    name: 'Starter',
    bestFor: 'Sole traders & first websites',
    price: '£99',
    period: '/month',
    maintainPrice: '£69',
    note: '14-day refund · 12-month Launch year, then rolling',
    description: 'A professional website with hosting, security, and support. Add features as you need them.',
    differentiators: [
      'Up to 5 pages',
      'Semi-custom design',
      'You provide the content',
      'Standard support',
      '2 change requests/month',
    ],
    cta: 'Get Started',
  },
  {
    name: 'Business',
    bestFor: 'Established small businesses',
    price: '£149',
    period: '/month',
    maintainPrice: '£99',
    note: '14-day refund · 12-month Launch year, then rolling',
    description: 'A fully custom site with content support. Includes 2 add-on features of your choice.',
    differentiators: [
      'Up to 10 pages',
      'Fully custom design',
      'Content assistance included',
      'Priority support',
      '2 add-on features included',
      '5 change requests/month',
    ],
    cta: 'Start Your Project',
    popular: true,
  },
  {
    name: 'Pro',
    bestFor: 'Premium / mature businesses',
    price: '£229',
    period: '/month',
    maintainPrice: '£159',
    note: '14-day refund · 12-month Launch year, then rolling',
    description: 'A bespoke site with dedicated content writing. Every feature we offer included.',
    differentiators: [
      'Unlimited pages',
      'Bespoke design',
      'We write your content',
      'Same-day support',
      'All 6 add-on features included',
      'Unlimited change requests',
    ],
    cta: 'Request a Quote',
  },
  {
    name: 'Commerce',
    bestFor: 'Selling products online',
    price: '£299',
    period: '/month',
    note: '+ 1.5% transaction fee · 14-day refund',
    description: 'Everything in Pro plus a fully managed online store powered by Stripe.',
    differentiators: [
      'Everything in Pro',
      'Stripe-hosted checkout',
      'Up to 100 products',
      'Order management & refunds',
      'Automated order emails',
      'Priority change requests',
    ],
    cta: 'Open Your Store',
    commerce: true,
  },
];

const addOns = [
  {
    icon: Calendar,
    title: 'Booking System',
    price: '20',
    period: '/mo',
    desc: 'Online booking forms with automated confirmations, approval workflow, and customer email notifications.',
  },
  {
    icon: Mail,
    title: 'Email Marketing',
    price: '20',
    period: '/mo',
    desc: 'Built-in email subscriptions with double opt-in, subscriber management, and GDPR compliance.',
  },
  {
    icon: BookOpen,
    title: 'Blog',
    price: '20',
    period: '/mo',
    desc: 'Publish blog posts with a rich text editor, manage drafts, and boost your SEO with fresh content.',
  },
  {
    icon: Images,
    title: 'Gallery',
    price: '15',
    period: '/mo',
    desc: 'Show off your work with a managed image gallery — captions, tags, drag-to-reorder, and a beautiful lightbox.',
  },
  {
    icon: CalendarDays,
    title: 'Events',
    price: '15',
    period: '/mo',
    desc: 'Create and manage events with dates, descriptions, and public listings on your website.',
  },
  {
    icon: UtensilsCrossed,
    title: 'Menu Management',
    price: '15',
    period: '/mo',
    desc: 'Manage your food or drink menu with categories, items, pricing, and display it beautifully on your site.',
  },
];

const faqs = [
  {
    question: 'How does the monthly plan work?',
    answer: 'You pay a fixed monthly fee with nothing upfront. You have 14 days from sign-up to cancel for any reason and get a full refund. After that you commit to your Launch year (12 months from sign-up). Year 1 is when we design, build, and onboard you, with full support and content help. From year 2 onwards your rate automatically drops to the Maintain price (around 30% lower) — your site stays live, secure, and supported — and the contract becomes rolling, cancellable any time with 30 days\' notice.',
  },
  {
    question: 'What’s included in every plan?',
    answer: 'Every plan includes a custom-designed website, mobile-responsive design, fast CDN hosting, SSL, firewall protection, SEO setup, Google Analytics, an admin portal branded to your business, contact form with email notifications, and ongoing support. Add-on features like bookings, blog, gallery and online store are available on top.',
  },
  {
    question: 'Can I add or remove features later?',
    answer: 'Absolutely. You can add features anytime as your business grows, and remove them if you no longer need them. Your monthly payment adjusts accordingly. No long-term commitment on individual features.',
  },
  {
    question: 'Do I own the website?',
    answer: 'You subscribe to our managed platform, similar to Squarespace or Wix — but with a custom-designed site and far more capability. Your domain name is always yours. If you decide to leave after your Launch year, your domain goes with you.',
  },
  {
    question: 'What happens if I cancel?',
    answer: 'For the first 14 days from sign-up you can cancel for any reason and get a full refund. After that you\'re committed to your Launch year (12 months from sign-up). From year 2 onwards the contract is rolling — cancel any time with 30 days\' notice, no exit fees. If you cancel during the Launch year (after the 14-day window) the remaining Launch-year fees become due, less anything we reasonably save and a discount for early payment. When you do leave, the website is taken offline, your domain stays yours, and we export any content you have created.',
  },
  {
    question: 'How long does it take to build?',
    answer: 'Most websites are live within 1–2 weeks. We’ll give you a clear timeline before we start. Pro and Commerce plans with bespoke design and content writing may take a little longer.',
  },
  {
    question: 'What’s the difference between the plans?',
    answer: 'Four plans, four fits. Starter is a brochure site — up to 5 pages, semi-custom design, you provide the content, and you can add features à la carte from £15/month. Business is our most popular plan — fully custom design, up to 10 pages, content assistance, and 2 add-on features of your choice included. Pro is for premium businesses — bespoke design, unlimited pages, we write your content, and all 6 add-on features are included. Commerce adds a fully managed Stripe-powered online store on top of Pro. The price drop in year 2 applies to Starter, Business, and Pro; Commerce stays at £299/month because the eCommerce work continues throughout.',
  },
  {
    question: 'How does the Commerce plan work?',
    answer: 'Commerce includes everything in Pro plus a Stripe-powered online store — product management, secure checkout, automated order emails, and refund handling. We handle Stripe Connect onboarding so you can take your first sale within days. The 1.5% transaction fee is on top of Stripe’s standard processing fees and helps cover order infrastructure.',
  },
  {
    question: 'Can I upgrade my plan later?',
    answer: 'Yes, you can move to a higher plan anytime. We’ll adjust your monthly payment and make the changes to your site — whether that’s more pages, a design refresh, content support, or opening your online store. Major upgrades may start a fresh Launch year for the new tier.',
  },
  {
    question: 'What’s a Launch year vs a Maintain year?',
    answer: 'Year 1 (Launch) is when most of the work happens — design, build, content, training, optimisation, plus your full tier allowance of change requests and support. From year 2 onwards (Maintain) your rate automatically drops by around 30% on Starter, Business, and Pro plans, your site keeps running with hosting, security, monitoring and platform updates, all toggled-on features stay live, and you get up to 30 minutes of small changes (text, images, swaps) each month. Bigger changes — new pages, redesigns, new features — are quoted on top at £45/hour or as part of a fresh Launch year. Commerce stays at £299/month in year 2+ because the eCommerce work (orders, refunds, payment ops support) continues throughout — it isn’t a "build then leave it" situation.',
  },
  {
    question: 'Can I pay for the Launch year upfront and save?',
    answer: 'Yes — pay your full Launch year upfront and you get a 12% discount. So Starter at £99/month becomes £1,045 paid up-front for 12 months (saving £143). Business becomes £1,573 (saving £215). Pro becomes £2,418 (saving £330). Commerce becomes £3,157 (saving £431). After your Launch year, your rate automatically drops to the Maintain price and you go back to monthly billing. The upfront discount is non-refundable beyond the first 14 days, but if you cancel within 14 days of launch you get a full refund.',
  },
];

const Pricing = () => {
  return (
    <div data-testid="pricing-page">
      {/* Hero */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="max-w-3xl mx-auto text-center">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-primary mb-6">
              Your Website, Your Way
            </h1>
            <p className="text-lg text-muted-foreground">
              {'£'}0 upfront. Pick your plan, add the features your business needs,
              and only pay for what you use.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-8 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {packages.map((pkg, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
              >
                <Card
                  className={`h-full relative flex flex-col ${
                    pkg.popular
                      ? 'border-accent border-2 shadow-xl'
                      : pkg.commerce
                        ? 'border-primary border-2 shadow-lg'
                        : 'border-border/50'
                  }`}
                >
                  {pkg.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-accent-foreground text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                      Most Popular
                    </div>
                  )}
                  {pkg.commerce && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap flex items-center gap-1.5">
                      <ShoppingBag className="w-3.5 h-3.5" /> Sell Online
                    </div>
                  )}
                  <CardContent className="p-6 md:p-7 flex flex-col flex-1">
                    <div className="mb-6">
                      <h2 className="font-heading text-2xl font-bold text-foreground mb-1">
                        {pkg.name}
                      </h2>
                      {pkg.bestFor && (
                        <p className="text-xs uppercase tracking-wider text-accent font-semibold mb-3">
                          {pkg.bestFor}
                        </p>
                      )}
                      <p className="text-sm text-muted-foreground mb-4">
                        {pkg.description}
                      </p>

                      <div className="flex items-baseline gap-1">
                        <span className="font-heading text-4xl font-bold text-primary">{pkg.price}</span>
                        <span className="text-lg text-muted-foreground">{pkg.period}</span>
                      </div>
                      {pkg.maintainPrice && (
                        <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-accent/10 border border-accent/30 px-3 py-1 text-xs font-semibold text-accent">
                          <ArrowDown className="w-3.5 h-3.5" strokeWidth={2.5} />
                          Drops to {pkg.maintainPrice}/mo from year 2
                        </div>
                      )}
                      <p className="text-muted-foreground text-xs mt-2">{pkg.note}</p>
                    </div>

                    <ul className="space-y-3 mb-8 flex-1">
                      {pkg.differentiators.map((item, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm">
                          <Check className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                          <span className="text-foreground">{item}</span>
                        </li>
                      ))}
                    </ul>

                    <Button
                      asChild
                      data-testid={`pricing-select-${pkg.name.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`w-full rounded-full py-6 ${
                        pkg.popular
                          ? 'bg-accent text-accent-foreground hover:bg-accent/90'
                          : 'bg-primary text-primary-foreground hover:bg-primary/90'
                      }`}
                    >
                      <Link to="/contact">{pkg.cta}</Link>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeInUp} className="text-center mt-10 max-w-2xl mx-auto space-y-2">
            <p className="text-muted-foreground text-sm">
              All plans include hosting, security, SEO, analytics, admin portal, and ongoing support.
              Add features below to tailor your site to your business.
            </p>
            <p className="text-muted-foreground text-sm">
              <span className="text-accent font-medium">Save 12%</span> by paying for your Launch year upfront.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Add-on Features */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4">
              Add the Features You Need
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              Every business is different. A salon needs bookings. A restaurant needs a menu.
              A photographer needs a gallery. Pick what fits yours {' — '} add or remove anytime.
            </p>
            <p className="text-sm text-muted-foreground/70 mt-3 italic">
              Pro and Commerce plans include all six features. Business includes 2; Starter clients add only what they need.
            </p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {addOns.map((addon, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.06 }}
              >
                <Card className="border-border/50 h-full">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                        <addon.icon className="w-5 h-5 text-accent" />
                      </div>
                      <div className="text-right">
                        <span className="font-heading text-xl font-bold text-primary">+{'£'}{addon.price}</span>
                        <span className="text-sm text-muted-foreground">{addon.period}</span>
                      </div>
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                      {addon.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">{addon.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Online Store — Commerce-tier-only feature */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mb-10"
          >
            <Card className="border-primary border-2 shadow-md bg-primary/5">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4 gap-4">
                  <div className="w-10 h-10 bg-primary text-primary-foreground rounded-lg flex items-center justify-center shrink-0">
                    <ShoppingBag className="w-5 h-5" />
                  </div>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground text-[11px] font-semibold uppercase tracking-wider px-3 py-1.5 whitespace-nowrap">
                    <ShoppingBag className="w-3.5 h-3.5" /> Commerce plan
                  </span>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                  Online Store
                </h3>
                <p className="text-sm text-muted-foreground">
                  Sell physical or digital products with a Stripe-powered storefront — products,
                  secure checkout, automated order emails, refund handling, and Stripe Connect
                  onboarding. Included with the Commerce plan.
                </p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Bundle Banner */}
          <motion.div {...fadeInUp}>
            <div className="bg-primary rounded-xl p-6 md:p-8 text-center">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-accent" />
                <h3 className="font-heading text-xl font-bold text-primary-foreground">
                  Want everything?
                </h3>
                <Sparkles className="w-5 h-5 text-accent" />
              </div>
              <p className="text-primary-foreground/80 mb-4 max-w-xl mx-auto">
                Pro includes all six add-on features at no extra cost {' — '} that’s
                {' '}<span className="font-bold text-accent">{'£'}105/month of features bundled in</span>{' '}
                with bespoke design and same-day support.
              </p>
              <Button
                asChild
                className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-8 py-5"
              >
                <Link to="/contact">See Pro Plan</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What's Always Included */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4">
              Included With Every Plan
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Before you add a single feature, every plan comes with everything
              you need for a professional online presence.
            </p>
          </motion.div>
          <motion.div {...fadeInUp} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {[
              'Custom-designed website',
              'Mobile responsive',
              'Fast CDN hosting',
              'SSL certificate',
              'Firewall (WAF) protection',
              'Branded admin portal',
              'Enquiries inbox',
              'Change request system',
              'Google Analytics dashboard',
              'Monthly performance report',
              'SEO setup & sitemap',
              'Schema.org structured data',
              'GDPR cookie consent banner',
              'Ongoing updates',
              'UK-based support',
              '£0 upfront',
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <Check className="w-5 h-5 text-accent shrink-0" />
                <span className="text-sm text-foreground">{item}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center mx-auto mb-4">
              <HelpCircle className="w-6 h-6 text-accent" />
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4">
              Common Questions
            </h2>
          </motion.div>
          <motion.div {...fadeInUp}>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-border/50">
                  <AccordionTrigger className="text-left font-heading font-semibold hover:text-accent">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              Not Sure What You Need?
            </h2>
            <p className="text-primary-foreground/80 text-lg mb-8 max-w-2xl mx-auto">
              No worries {' — '} get in touch and we'll help you figure out the right plan
              and features for your business. No pressure, just honest advice.
            </p>
            <Button
              asChild
              data-testid="pricing-cta-btn"
              className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full px-8 py-6 text-lg"
            >
              <Link to="/contact">Let's Chat</Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
